import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  CheckCircle, 
  AlertCircle, 
  XCircle,
  RefreshCw
} from 'lucide-react';
import { api } from '../api';
import type { HealthStatus } from '../types';

export function HealthDashboard() {
  const navigate = useNavigate();
  const [health, setHealth] = useState<HealthStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  useEffect(() => {
    loadHealth();
    const interval = setInterval(loadHealth, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const loadHealth = async () => {
    try {
      const data = await api.getDetailedHealth();
      setHealth(data);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Failed to load health:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle size={20} color="var(--doyle-green)" />;
      case 'degraded':
        return <AlertCircle size={20} color="var(--color-warning)" />;
      case 'unhealthy':
        return <XCircle size={20} color="var(--doyle-red)" />;
      default:
        return <AlertCircle size={20} color="var(--text-muted)" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const className = 
      status === 'healthy' ? 'badge-success' :
      status === 'degraded' ? 'badge-warning' :
      'badge-error';
    
    return (
      <span className={`badge ${className}`}>
        {status.toUpperCase()}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '100vh' 
      }}>
        <div className="spinner" style={{ width: 40, height: 40 }} />
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: 'var(--bg-primary)',
      padding: 'var(--spacing-lg)'
    }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          marginBottom: 'var(--spacing-lg)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)' }}>
            <button 
              className="btn-icon btn-ghost"
              onClick={() => navigate('/chat')}
            >
              <ArrowLeft size={20} />
            </button>
            <h1>System Health</h1>
          </div>
          <button 
            className="btn-secondary"
            onClick={loadHealth}
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>

        {/* Overall Status */}
        <div className="card" style={{ 
          marginBottom: 'var(--spacing-lg)',
          padding: 'var(--spacing-xl)'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            marginBottom: 'var(--spacing-md)'
          }}>
            <h2>Overall System Status</h2>
            {health && getStatusBadge(health.status)}
          </div>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
            Last updated: {lastUpdated.toLocaleTimeString()}
          </p>
        </div>

        {/* Component Status Grid */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: 'var(--spacing-md)'
        }}>
          {health && Object.entries(health.components).map(([name, component]) => (
            <div key={name} className="card">
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                marginBottom: 'var(--spacing-sm)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
                  {getStatusIcon(component.status)}
                  <h3 style={{ fontSize: '1rem' }}>
                    {name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </h3>
                </div>
                {getStatusBadge(component.status)}
              </div>
              
              {component.message && (
                <p style={{ 
                  fontSize: '0.875rem', 
                  color: 'var(--text-secondary)',
                  marginBottom: 'var(--spacing-sm)'
                }}>
                  {component.message}
                </p>
              )}
              
              {component.latency_ms !== undefined && (
                <div style={{ 
                  fontSize: '0.75rem', 
                  color: 'var(--text-muted)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--spacing-xs)'
                }}>
                  <span>Response time:</span>
                  <span style={{ 
                    fontWeight: 600,
                    color: component.latency_ms < 1000 ? 'var(--doyle-green)' : 
                           component.latency_ms < 3000 ? 'var(--color-warning)' : 
                           'var(--doyle-red)'
                  }}>
                    {component.latency_ms}ms
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Legend */}
        <div className="card" style={{ marginTop: 'var(--spacing-lg)' }}>
          <h3 style={{ marginBottom: 'var(--spacing-md)', fontSize: '1rem' }}>Status Legend</h3>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 'var(--spacing-md)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
              <CheckCircle size={16} color="var(--doyle-green)" />
              <span style={{ fontSize: '0.875rem' }}>Healthy - All systems operational</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
              <AlertCircle size={16} color="var(--color-warning)" />
              <span style={{ fontSize: '0.875rem' }}>Degraded - Performance issues</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
              <XCircle size={16} color="var(--doyle-red)" />
              <span style={{ fontSize: '0.875rem' }}>Unhealthy - Service unavailable</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
